import {
  hasAnyPermission,
  WERKKWALITEIT_BROAD_PERMISSIONS,
  WERKKWALITEIT_PERMISSIONS,
  WERKKWALITEIT_SETTINGS_PERMISSIONS,
} from '@/lib/permissions';

/**
 * SPEC-werkkwaliteit-herontwerp CAP-10 (EXPERIENCE.md, Information Architecture) — one route stem,
 * five views, and who sees which.
 *
 * The order is Team · Eigen werk · Nakijkers · AI · Instellingen (besluit 31-10-2026): whoever may
 * see the team starts there, which is why `werkkwaliteitStart` sends that right to Team. `/werkkwaliteit`
 * itself stays Mijn feedback, so existing bookmarks keep working. **One view gets no tab bar**
 * (UX-DR2): with only `werkkwaliteit.read.own` there is nothing to switch between, and a lone tab
 * would advertise that other views exist for other people.
 *
 * The person detail (`/werkkwaliteit/medewerkers/:userId`) is deliberately not a tab. It is a
 * destination inside Team, which is why `activeWorkQualityTab` keeps that tab lit.
 */
export interface WorkQualityTab {
  id: 'team' | 'own' | 'nakijkers' | 'ai' | 'settings';
  to: string;
  labelKey: string;
  testId: string;
}

export const WERKKWALITEIT_STAM = '/werkkwaliteit';
export const WERKKWALITEIT_MEDEWERKERS = '/werkkwaliteit/medewerkers';
export const WERKKWALITEIT_NAKIJKERS = '/werkkwaliteit/nakijkers';
export const WERKKWALITEIT_AI = '/werkkwaliteit/ai';
export const WERKKWALITEIT_INSTELLINGEN = '/werkkwaliteit/instellingen';

/** The address parameters that travel along with every tab switch; anything else stays behind. */
const MEEREIZEND = ['week', 'werksoort'] as const;

const ALLE_TABS: readonly (WorkQualityTab & { vereist: readonly string[] })[] = [
  { id: 'team', to: WERKKWALITEIT_MEDEWERKERS, labelKey: 'qa.bediening.tabbalk.team', testId: 'werkkwaliteit-tab-team', vereist: WERKKWALITEIT_BROAD_PERMISSIONS },
  { id: 'own', to: WERKKWALITEIT_STAM, labelKey: 'qa.bediening.tabbalk.own', testId: 'werkkwaliteit-tab-own', vereist: WERKKWALITEIT_PERMISSIONS },
  { id: 'nakijkers', to: WERKKWALITEIT_NAKIJKERS, labelKey: 'qa.bediening.tabbalk.nakijkers', testId: 'werkkwaliteit-tab-nakijkers', vereist: WERKKWALITEIT_BROAD_PERMISSIONS },
  { id: 'ai', to: WERKKWALITEIT_AI, labelKey: 'qa.bediening.tabbalk.ai', testId: 'werkkwaliteit-tab-ai', vereist: WERKKWALITEIT_BROAD_PERMISSIONS },
  { id: 'settings', to: WERKKWALITEIT_INSTELLINGEN, labelKey: 'qa.bediening.tabbalk.settings', testId: 'werkkwaliteit-tab-settings', vereist: WERKKWALITEIT_SETTINGS_PERMISSIONS },
];

export function workQualityTabs(permissions: readonly string[] | undefined): WorkQualityTab[] {
  return ALLE_TABS
    .filter(({ vereist }) => hasAnyPermission(permissions, vereist))
    .map(({ id, to, labelKey, testId }) => ({ id, to, labelKey, testId }));
}

/** Where the menu item Werkkwaliteit opens: Team for whoever may see the team, Mijn feedback otherwise. */
export function werkkwaliteitStart(permissions: readonly string[] | undefined): string {
  return hasAnyPermission(permissions, WERKKWALITEIT_BROAD_PERMISSIONS) ? WERKKWALITEIT_MEDEWERKERS : WERKKWALITEIT_STAM;
}

/** `to` with week and werksoort from `search`, in that order; a missing parameter stays out. */
export function metMeereizend(to: string, search: string): string {
  const huidig = new URLSearchParams(search);
  const mee = new URLSearchParams();
  for (const naam of MEEREIZEND) {
    const waarde = huidig.get(naam);
    if (waarde !== null) mee.set(naam, waarde);
  }
  const query = mee.toString();
  return query === '' ? to : `${to}?${query}`;
}

/** Segment-safe, the way the sidebar already is: `/werkkwaliteit/medewerkersoverzicht` is not it. */
function isBinnen(pathname: string, stam: string): boolean {
  return pathname === stam || pathname.startsWith(`${stam}/`);
}

export function activeWorkQualityTab(pathname: string): WorkQualityTab['id'] {
  if (isBinnen(pathname, WERKKWALITEIT_MEDEWERKERS)) return 'team';
  if (isBinnen(pathname, WERKKWALITEIT_NAKIJKERS)) return 'nakijkers';
  if (isBinnen(pathname, WERKKWALITEIT_AI)) return 'ai';
  if (isBinnen(pathname, WERKKWALITEIT_INSTELLINGEN)) return 'settings';
  return 'own';
}
